import dayjs from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  CoachMetricsParams,
  ParticipantsListApiResponse,
} from "../../../api/services/program/models";
import {
  useCoachMetricsMutation,
  useGetMyMeetingsQuery,
  useGetParticipantsList,
} from "../../../api/services/program/programApis";
import { AutoCompleteOption } from "../../../components";
import {
  COACH_METRICS_DATE_RANGE,
  DEBOUNCE_THRESHOLD,
  PAGE_IDS,
  TIMEOUT_300,
  ZERO,
} from "../../../helpers/constants";
import { getFullName, isNotEmpty } from "../../../helpers/utils";
import { useDebounce } from "../../../hooks/useDebounce";
import { useSiteCore } from "../../../hooks/useSiteCore";
import { useToast } from "../../../hooks/useToast";
import { toggleLoader } from "../../../redux/slices/appState/appStateSlice";
import { ParticipantAddActivity } from "../../createProgram/components/createProgramTabs/tabs/participant/components/participantAddActivity/ParticipantAddActivity";
import {
  CoachMetricsCardTypes,
  MY_MEETINGS_TYPE_FILTER_KEYS,
  MY_MEETINGS_UPCOMING_PERIOD_FILTER_KEYS,
  MyMeetingsFilterFormKeys,
} from "../modelsAndSchemas/Models";
import { CPDashboardMainWrapper } from "./CPDashboardMain.style";
import {
  CoachMetricsDetails,
  CPDashboardCoachMetrics,
} from "./components/cpDashboardCoachMetrics/CPDashboardCoachMetrics";
import { CPDashboardHeader } from "./components/cpDashboardHeader/CPDashboardHeader";
import { MyCaseloads } from "./components/myCaseLoads/MyCaseLoads";
import { MyCaseLoadsVsEngagements } from "./components/myCaseLoadsVsEngagements/MyCaseLoadsVsEngagements";
import { MyMeetings } from "./components/myMeetings/MyMeetings";
import { ParticipantLanded } from "./components/participantLanded/ParticipantLanded";

const pageId = PAGE_IDS.COACH_PORTAL_DASHBOARD_MAIN;
export const CPDashboardMain: FC = () => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { commonLabels } = useSiteCore();
  const [showParticipantsLanded, setShowParticipantsLanded] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showCaseLoadsVsEngagements, setShowCaseLoadsVsEngagements] =
    useState(false);
  const [inputValue, setInputValue] = useState("");
  const debouncedValue = useDebounce(inputValue, TIMEOUT_300);
  const [participantListOption, setParticipantListOption] = useState<
    AutoCompleteOption[]
  >([]);

  const formDefaultValues = {
    [MyMeetingsFilterFormKeys.type]:
      MY_MEETINGS_TYPE_FILTER_KEYS.UPCOMING_SESSION,
    [MyMeetingsFilterFormKeys.period]:
      MY_MEETINGS_UPCOMING_PERIOD_FILTER_KEYS.TODAY,
  };

  const methods = useForm({
    mode: "all",
    defaultValues: formDefaultValues,
  });

  const {
    data: participantsList,
    isFetching: isParticipantsListPending,
    isError: isParticipantsListError,
    refetchWithParams: refetchParticipantsList,
  } = useGetParticipantsList();

  const {
    data: coachMetricsData,
    isPending: isCoachMetricsPending,
    isError: isCoachMetricsError,
    isSuccess: isCoachMetricsSuccess,
    mutateAsync: getCoachMetrics,
  } = useCoachMetricsMutation();

  const {
    data: myMeetingsData,
    isPending: isMyMeetingsPending,
    isError: isMyMeetingsError,
  } = useGetMyMeetingsQuery();

  useEffect(() => {
    const { fromDate, toDate } = getDateRangeByMetricsType(
      COACH_METRICS_DATE_RANGE.SIX_MONTH,
    );

    const metricParams: CoachMetricsParams = {
      fromDate,
      toDate,
    };

    getCoachMetrics(metricParams);
  }, []);

  useEffect(() => {
    if (debouncedValue.length >= DEBOUNCE_THRESHOLD) {
      refetchParticipantsList({ name: debouncedValue });
    }
    setParticipantListOption([]);
  }, [debouncedValue]);

  useEffect(() => {
    if (participantsList && isNotEmpty(participantsList)) {
      const options: AutoCompleteOption[] = participantsList.map(
        (item: ParticipantsListApiResponse) => ({
          id: item.programId,
          label: getFullName(item.firstName, item.lastName),
        }),
      );
      setParticipantListOption(options);
    } else {
      setParticipantListOption([]);
    }
  }, [participantsList]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        CPDashboardLoading: {
          isVisible:
            isParticipantsListPending ||
            isCoachMetricsPending ||
            isMyMeetingsPending,
        },
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          CPDashboardLoading: {
            isVisible: false,
          },
        }),
      );
    };
  }, [isParticipantsListPending, isCoachMetricsPending, isMyMeetingsPending]);

  useEffect(() => {
    if (isParticipantsListError || isCoachMetricsError || isMyMeetingsError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ CPDashboardLoading: { isVisible: false } }));
    }
  }, [isParticipantsListError, isCoachMetricsError, isMyMeetingsError]);

  const totalHoursSpentValue = useMemo(() => {
    const hours = coachMetricsData?.coachingActivities?.totalHoursSpent;
    if (!hours) return `${ZERO} ${commonLabels.hour}`;
    const suffix = hours > 1 ? commonLabels.hours : commonLabels.hour;
    return `${hours} ${suffix}`;
  }, [coachMetricsData]);

  const coachMetricsDetails: CoachMetricsDetails = useMemo(() => {
    if (
      isCoachMetricsSuccess &&
      coachMetricsData &&
      isNotEmpty(coachMetricsData)
    ) {
      return {
        assignedProgramsCount:
          coachMetricsData.caseLoadsVsEngagements.assignedProgramsCount?.toString(),
        programsWithoutEngagementCount:
          coachMetricsData.caseLoadsVsEngagements.programsWithoutEngagementCount?.toString(),
        completedActivitiesCount:
          coachMetricsData.coachingActivities.completedActivitiesCount?.toString(),
        totalHoursSpent: totalHoursSpentValue,
        participantsLandedCount:
          coachMetricsData.participantsLandedCount?.toString(),
      };
    }

    return {
      assignedProgramsCount: ZERO,
      programsWithoutEngagementCount: ZERO,
      completedActivitiesCount: ZERO,
      totalHoursSpent: totalHoursSpentValue,
      participantsLandedCount: ZERO,
    };
  }, [isCoachMetricsSuccess, coachMetricsData]);

  const handleCardClick = (type: CoachMetricsCardTypes) => {
    switch (type) {
      case "participants_landed":
        setShowParticipantsLanded(true);
        break;

      case "caseload_vs_engagement":
        setShowCaseLoadsVsEngagements(true);
        break;

      default:
        break;
    }
  };

  const handleAddActivityModalClose = () => {
    setShowActivityModal(false);
    setInputValue("");
  };

  const handleAddActivity = () => setShowActivityModal(true);

  const getDateRangeByMetricsType = (range: string) => {
    let fromDate = "";
    let toDate = "";

    if (range === COACH_METRICS_DATE_RANGE.CURRENT_MINUS_ONE_YEAR) {
      fromDate = dayjs().utc().subtract(1, "year").startOf("year").toISOString();
      toDate = dayjs().utc().subtract(1, "year").endOf("year").toISOString();
    } else if (range === COACH_METRICS_DATE_RANGE.CURRENT_MINUS_TWO_YEAR) {
      fromDate = dayjs().utc().subtract(2, "year").startOf("year").toISOString();
      toDate = dayjs().utc().subtract(2, "year").endOf("year").toISOString();
    } else if (range === COACH_METRICS_DATE_RANGE.SIX_MONTH) {
      fromDate = dayjs().utc().subtract(6, "month").startOf("day").toISOString();
      toDate = dayjs().utc().endOf("day").toISOString();
    } else if (range === COACH_METRICS_DATE_RANGE.YEAR_TILL_DATE) {
      fromDate = dayjs().utc().startOf("year").toISOString();
      toDate = dayjs().utc().endOf("day").toISOString();
    }

    return { fromDate, toDate };
  };

  const handleDateRangeChange = (range: string) => {
    const { fromDate, toDate } = getDateRangeByMetricsType(range);

    const metricParams: CoachMetricsParams = {
      fromDate,
      toDate,
    };

    getCoachMetrics(metricParams);
  };

  return (
    <FormProvider {...methods}>
      <CPDashboardMainWrapper>
        <CPDashboardHeader onAddActivityClick={handleAddActivity} />
        <CPDashboardCoachMetrics
          coachMetricsDetails={coachMetricsDetails}
          onCardClick={handleCardClick}
          onDateRangeChange={handleDateRangeChange}
        />
        <MyCaseloads ageingCountsDetails={coachMetricsData?.ageingCounts} />
        <MyMeetings meetingsData={myMeetingsData} />
        {showParticipantsLanded && (
          <ParticipantLanded
            open={showParticipantsLanded}
            onClose={() => setShowParticipantsLanded(false)}
          />
        )}

        {showCaseLoadsVsEngagements && (
          <MyCaseLoadsVsEngagements
            open={showCaseLoadsVsEngagements}
            onClose={() => setShowCaseLoadsVsEngagements(false)}
          />
        )}

        {showActivityModal && (
          <ParticipantAddActivity
            show={showActivityModal}
            handleCloseModal={handleAddActivityModalClose}
            pageId={pageId}
            getListOfActivitiesApi={handleAddActivityModalClose}
            isEditActivity={false}
            isFromCoachDashboard
            participantListOption={participantListOption}
            inputValue={inputValue}
            setInputValue={setInputValue}
          />
        )}
      </CPDashboardMainWrapper>
    </FormProvider>
  );
};
